import React, { useState, useEffect, useRef, useMemo } from 'react';

import { useResizeObserver } from 'usehooks-ts';
import { saveAs } from 'file-saver';

import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Table, Flex, notification, Input, Select, DatePicker, InputNumber } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { LoadingOutlined, ReloadOutlined, FileExcelOutlined } from '@ant-design/icons';

import Processing from '@controls/processing';
import Filter from '@controls/filter/filter';
import Toolbar from '@controls/toolbar/toolbar';
import IBaseComponentProps from '@components/base-component-props';

import { userLoaded, setFilter } from '@store/actions';

import { exception } from '@extensions/notification';
import { delayAction, dataURItoBlob } from '@extensions/utils';

import { useAppDispatch, useAppSelector } from '@store/hooks';

import { IClientActivityReport } from '@src/core/entities/client-activity-report';
import { IClientActivityReportFilter } from '@src/core/entities/client-activity-report-filter';

import { ICity } from '@entities/city';
import { IUser } from '@entities/user';
import { UserType } from '@src/core/enums/user-type';

dayjs.extend(utc);

const filterContext: string = 'ClientsActivityReport';

const ClientsActivityReport = (props: IBaseComponentProps) => {
    const { modulePath, serverFetch } = props;

    const { RangePicker } = DatePicker;

    const initFilter: IClientActivityReportFilter = {
        dateFrom: dayjs().add(-6, 'month').utc(true).startOf('day'),
    };

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const d = useAppDispatch();

    const [api, contextHolder] = notification.useNotification();

    const [reportLines, setReportLines] = useState<Array<IClientActivityReport>>([]);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [loadingUsers, setLoadingUsers] = useState<boolean>(false);

    const filter = useAppSelector<IClientActivityReportFilter>((s) => s.filters[filterContext]);

    const [showFilter, setShowFilter] = useState<boolean>(true);
    const [exporting, setExporting] = useState<boolean>(false);

    const [cities, setCities] = useState<Array<ICity>>([]);
    const [suppliers, setSuppliers] = useState<Array<IUser>>([]);

    useEffect(() => {
        let cleanup = false;

        const fetchData = async () => {
            const promises = [
                await serverFetch('cities', { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения городов', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('users', { method: 'GET', queryParams: { types: [UserType.Supplier] /* isActiveOnly: true  */ } })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения поставщиков', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setCities(result[0][0] || []);
                setSuppliers(result[0][1] || []);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, []);

    useEffect(() => {
        if (!refreshRequired) return;

        let cleanup = false;

        const fetchData = async () => {
            setLoadingUsers(true);

            setReportLines([]);

            await serverFetch('reports/clientsactivity', { method: 'GET', queryParams: filter })
                .then((data: Array<IClientActivityReport>) => {
                    if (cleanup) return;

                    setReportLines(data || []);
                })
                .catch((ex: any) => {
                    exception(api, 'Ошибка получения отчета по клиентам', ex, () => d(userLoaded(undefined)));
                });

            if (cleanup) return;

            setLoadingUsers(false);
            setRefreshRequired(false);
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));

            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    const onExport = () => {
        var exportFilter: IClientActivityReportFilter = { ...filter };

        setExporting(true);

        serverFetch('reports/clientsactivity/export', { method: 'GET', queryParams: exportFilter })
            .then((data: any) => {
                setExporting(false);

                const fileBody = `data:${data.contentType};base64,${data.fileContents}`;
                saveAs(dataURItoBlob(fileBody), data.fileDownloadName);
            })
            .catch((ex: any) => {
                setExporting(false);
                exception(api, 'Ошибка выгрузки отчета по клиентам', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        disabled: loadingUsers,
                        key: 'refresh',
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Выгрузить отчет по клиентам в Excel',
                        key: 'exportToExcel',
                        icon: <FileExcelOutlined />,
                        onClick: () => onExport(),
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderFilter = () => {
        return (
            <Filter
                display={showFilter}
                items={[
                    <InputNumber
                        key='code'
                        placeholder='ID клиента'
                        value={filter?.consigneeCode}
                        style={{ width: 130 }}
                        onChange={(value) => {
                            d(setFilter({ ...filter, consigneeCode: value }, filterContext));
                        }}
                    />,
                    <Input
                        key='markingCode'
                        placeholder='Маркировка'
                        style={{ width: 230 }}
                        value={filter?.markingCode}
                        onChange={(data) => {
                            d(setFilter({ ...filter, markingCode: data.target.value }, filterContext));
                        }}
                    />,
                    <Input
                        key='searchText'
                        placeholder='Контрагент'
                        value={filter?.searchText}
                        style={{ width: 230 }}
                        onChange={(data) => {
                            d(setFilter({ ...filter, searchText: data.target.value }, filterContext));
                        }}
                    />,
                    <Select
                        key='city'
                        placeholder='Город доставки'
                        maxTagCount='responsive'
                        value={filter?.cityId}
                        allowClear={true}
                        style={{ width: 200 }}
                        onChange={(value) => d(setFilter({ ...filter, cityId: value }, filterContext))}
                        showSearch={{
                            filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                            filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                        }}
                        options={cities.map((c) => {
                            return { value: c.id, label: c.name };
                        })}
                    />,
                    <Select
                        maxTagCount='responsive'
                        key='supplier'
                        placeholder='Поставщик'
                        value={filter?.supplierIds}
                        size='middle'
                        mode='multiple'
                        allowClear={true}
                        style={{ width: 200 }}
                        onChange={(value) => d(setFilter({ ...filter, supplierIds: value }, filterContext))}
                        showSearch={{
                            filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                            filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                        }}
                        options={suppliers.map((c) => {
                            return { value: c.id, label: c.login };
                        })}
                    />,
                    <RangePicker
                        style={{ width: 220 }}
                        allowEmpty={[true, true]}
                        key='departure'
                        format='DD.MM.YYYY'
                        placeholder={['Заказ с', 'до']}
                        cellRender={(current) => {
                            return <div className='ant-picker-cell-inner'>{(current as Dayjs).date()}</div>;
                        }}
                        value={[filter?.dateFrom ? dayjs(filter?.dateFrom) : null, filter?.dateTo ? dayjs(filter?.dateTo) : null]}
                        onChange={(value) => {
                            if (!value) {
                                d(
                                    setFilter(
                                        {
                                            ...filter,
                                            dateFrom: undefined,
                                            dateTo: undefined,
                                        },
                                        filterContext,
                                    ),
                                );

                                return;
                            }

                            d(
                                setFilter(
                                    {
                                        ...filter,
                                        dateFrom: value[0] ? dayjs(value[0]).utc(true).startOf('day').toString() : undefined,
                                        dateTo: value[1] ? dayjs(value[1]).utc(true).endOf('day').toString() : undefined,
                                    },
                                    filterContext,
                                ),
                            );
                        }}
                    />,
                ]}
                onClear={() => d(setFilter(initFilter, filterContext))}
            />
        );
    };

    const dayKey = (value?: Dayjs | string) => dayjs(value).format('YYYY-MM-DD');

    const dateKeys = useMemo(() => {
        const keys = new Set<string>();
        reportLines.forEach((r) => r.lines?.forEach((l) => l.dateOn && keys.add(dayKey(l.dateOn))));

        return Array.from(keys).sort();
    }, [reportLines]);

    const qtyByClient = useMemo(() => {
        const map = new Map<string, Map<string, number>>();
        reportLines.forEach((r) => {
            const days = new Map<string, number>();
            r.lines?.forEach((l) => {
                if (!l.dateOn) return;
                const key = dayKey(l.dateOn);
                days.set(key, (days.get(key) ?? 0) + (l.qty ?? 0));
            });
            map.set(r.id, days);
        });

        return map;
    }, [reportLines]);

    const columns = useMemo(() => {
        const result: ColumnsType<IClientActivityReport> = [
            {
                title: 'ID',
                width: 80,
                fixed: 'left',
                dataIndex: 'consigneCode',
                align: 'center',
            },
            {
                title: 'Маркировка',
                fixed: 'left',
                dataIndex: 'allMarkings',
                width: 250,
            },
            {
                title: 'Город',
                fixed: 'left',
                width: 200,
                dataIndex: 'cityName',
            },
            {
                title: 'Поставщик',
                dataIndex: 'supplierName',
                onCell: (record) => ({
                    style: {
                        fontWeight: 600,
                    },
                }),
                width: 130,
            },
            {
                title: 'Телефон',
                width: 150,
                render: (_: any, record: IClientActivityReport) => {
                    return (
                        <Flex style={{ width: '100%' }} vertical>
                            {record.phone && <div>{record.phone}</div>}
                            {record.phone2 && <div>{record.phone2}</div>}
                        </Flex>
                    );
                },
            },
            {
                title: 'Контрагент',
                width: 300,
                render: (_: any, record: IClientActivityReport) => {
                    const items = record.counterparties?.split(',');

                    return (
                        <Flex style={{ width: '100%' }} vertical>
                            {items?.map((item) => {
                                return item && <div>{item}</div>;
                            })}
                        </Flex>
                    );
                },
            },
        ];

        dateKeys.forEach((key) => {
            result.push({
                title: dayjs(key).format('DD.MM.YYYY'),
                key,
                width: 110,
                align: 'center',
                onCell: (record) => {
                    const days = qtyByClient.get(record.id);
                    if (days?.get(key)) return {};

                    // highlight empty cells only after the client's last qty (scanning right to left until the first qty)
                    const lastQtyKey = [...dateKeys].reverse().find((k) => days?.get(k));
                    return !lastQtyKey || key > lastQtyKey ? { style: { backgroundColor: '#FFC7CE' } } : {};
                },
                render: (_, record) => qtyByClient.get(record.id)?.get(key) || '',
            });
        });

         result.push({});
         
        return result;
    }, [dateKeys, qtyByClient, modulePath]);

    const renderTable = () => {
        return (
            <Table
                virtual={true}
                rowKey='id'
                size='small'
                columns={columns}
                dataSource={reportLines}
                loading={{
                    spinning: loadingUsers,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                pagination={false}
                scroll={{ x: 470 + dateKeys.length * 110, y: height ?? 0 }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 240px - ${filterHeight}px)` }}>
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            {exporting && <Processing title='Выгрузка отчета по клиентам...' />}

            {contextHolder}
        </div>
    );
};

export default ClientsActivityReport;
